import { routes } from '@communities/routes';
import { Breadcrumb, type BreadcrumbItem, Container } from '@soteria/design-system-ve';
import { useMemo } from 'react';
import { Link, useMatches } from 'react-router-dom';

import * as styles from './Breadcrumbs.css';

/**
 * Breadcrumb handle type for route configuration
 * Supports both static strings and functions that receive loader data
 */
export interface BreadcrumbHandle {
  breadcrumb?: string | ((data: unknown) => string);
}

/**
 * Community data structure extracted from loader data
 */
interface CommunityData {
  communityGroupId: string;
  communityGroup?: { name?: string };
}

/**
 * Get the correct pathname for a breadcrumb based on its pathname and available data
 */
function getBreadcrumbPath(pathname: string, communityGroupId?: string): string {
  // If we're on the /communities parent route and have a community group ID,
  // link to the community group detail page instead
  if (pathname === '/communities' && communityGroupId) {
    return routes.communityGroups.detail(communityGroupId);
  }

  return pathname;
}

/**
 * Find community data from any match in the hierarchy
 * Searches from deepest to shallowest match
 */
function findCommunityData(matches: ReturnType<typeof useMatches>): CommunityData | null {
  for (let i = matches.length - 1; i >= 0; i--) {
    const loaderData = matches[i].data as Record<string, unknown> | undefined;
    const community = loaderData?.community as CommunityData | undefined;

    if (community?.communityGroupId) {
      return community;
    }
  }

  return null;
}

/**
 * Breadcrumbs navigation component (Core wrapper)
 *
 * Uses React Router v7's useMatches() to build breadcrumb trail from route handles.
 * Reads data from route loaders via match.data and applies domain-specific business logic.
 *
 * This component wraps the generic design system Breadcrumb component and handles:
 * - React Router integration
 * - Route handle processing
 * - Community data extraction
 * - Path transformations
 * - Home breadcrumb injection
 */
export function Breadcrumbs() {
  const matches = useMatches();

  // Memoize breadcrumb computation to avoid recalculating on every render
  const breadcrumbItems = useMemo(() => {
    // Find community data from any match (for parent routes that need child data)
    const communityData = findCommunityData(matches);

    // Build breadcrumbs from matches that have breadcrumb handles
    const breadcrumbs: BreadcrumbItem[] = matches
      .filter(match => {
        const handle = match.handle as BreadcrumbHandle | undefined;

        return Boolean(handle?.breadcrumb);
      })
      .map((match, index) => {
        const handle = match.handle as BreadcrumbHandle;

        // Determine data to pass to breadcrumb function
        // Prefer current match data, fall back to community data from hierarchy
        const dataToPass = match.data || (communityData ? { community: communityData } : undefined);

        // Generate breadcrumb label
        const label = typeof handle.breadcrumb === 'function' ? handle.breadcrumb(dataToPass) : (handle.breadcrumb ?? '');

        // Determine correct pathname (may redirect /communities to community group detail)
        const pathname = getBreadcrumbPath(match.pathname, communityData?.communityGroupId);

        // Create unique ID for React key (combine index and pathname for uniqueness)
        const id = `${index}-${pathname}`;

        return {
          label,
          href: pathname,
          id,
        };
      });

    // Check if we're on a community-groups route to avoid duplicate home breadcrumb
    const isOnCommunityGroupsRoute = breadcrumbs.some(crumb => crumb.href?.startsWith('/community-groups'));

    // Prepend "Community Groups" home breadcrumb only if not already present
    if (isOnCommunityGroupsRoute) {
      return breadcrumbs;
    }

    return [
      {
        label: 'Community Groups',
        href: routes.communityGroups.root,
        id: 'home-community-groups',
      },
      ...breadcrumbs,
    ];
  }, [matches]);

  return (
    <Container padding="none" size="xxl">
      <div className={styles.breadcrumbContainer}>
        <Breadcrumb items={breadcrumbItems} renderLink={(item: BreadcrumbItem) => <Link to={item.href ?? '/'}>{item.label}</Link>} />
      </div>
    </Container>
  );
}
